import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import AwardBadge from "@/components/AwardBadge";
import { MapPin, Phone, ArrowRight, Mountains, PhoneCall } from "@phosphor-icons/react";
import { HERO_BG, CLIENT_RED_SHIRT, SCALED_LOGO, CALI_SPECIALIST } from "./constants";

export default function Hero({ c, onSignup, onNextMove }) {
  return (
    <header id="top" className="relative overflow-hidden">
      <div className="absolute inset-0">
        <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-cvc-navy-deep/90 via-cvc-navy/80 to-cvc-navy/65" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-16 pb-20 md:pt-24 md:pb-32">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          {/* LEFT — hero copy */}
          <div className="lg:col-span-7">
            <p className="text-xs tracking-[0.3em] uppercase font-body font-semibold text-cvc-cream/80 flex items-center gap-2">
              <Mountains size={16} weight="duotone" /> {c("hero_tagline", "Serving Logan Since 2018")}
            </p>
            <h1 data-testid="hero-headline" className="font-heading text-4xl sm:text-5xl lg:text-6xl text-cvc-cream mt-6 leading-[1.05] font-medium">
              {c("hero_headline", "Let's build a business that runs without you.")}
            </h1>
            <p data-testid="hero-sub" className="font-body text-lg text-cvc-cream/90 mt-7 max-w-2xl leading-relaxed">
              {c("hero_subheadline",
                "Cache Valley Consulting helps Logan, UT owners scale up from owner-operator to a fully-delegated, turnkey asset — so your entrepreneurial story has a great ending.")}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Button
                data-testid="hero-signup-cta"
                onClick={onSignup}
                className="bg-cvc-red hover:bg-cvc-red-deep text-white rounded-full px-8 py-6 h-auto text-base font-body"
              >
                Check availability <ArrowRight size={18} weight="bold" className="ml-2" />
              </Button>
              <a href="#approach">
                <Button
                  variant="outline"
                  className="bg-transparent border-cvc-cream/60 text-cvc-cream hover:bg-cvc-cream hover:text-cvc-navy rounded-full px-8 py-6 h-auto text-base font-body"
                >
                  See the approach
                </Button>
              </a>
              <Button
                data-testid="hero-nextmove-cta"
                variant="outline"
                onClick={onNextMove}
                className="bg-transparent border-cvc-cream/60 text-cvc-cream hover:bg-cvc-cream hover:text-cvc-navy rounded-full px-8 py-4 h-auto text-base font-body flex-col gap-0.5"
              >
                <span className="uppercase tracking-wider">Free Business Diag</span>
                <span data-testid="hero-nextmove-duration" className="text-xs opacity-75">(3 minutes)</span>
              </Button>
            </div>

            {/* Hero proof block — centered under the CTAs, photo slightly smaller, meta on left + photo on right */}
            <div className="mt-10 flex flex-col sm:flex-row-reverse items-center justify-center gap-7 sm:gap-10">
              <div className="relative shrink-0">
                <div className="absolute -inset-2 bg-cvc-cream/10 rounded-2xl rotate-[-1.5deg]" />
                <img
                  src={CLIENT_RED_SHIRT}
                  alt="Jonathan working through a sales-and-training plan with a Cache Valley client owner."
                  loading="lazy"
                  data-testid="hero-client-photo"
                  className="relative w-64 sm:w-72 lg:w-80 aspect-[4/3] object-cover rounded-xl border-2 border-cvc-cream/20 shadow-2xl"
                />
              </div>
              <div className="flex flex-col items-center gap-4 text-cvc-cream/85 font-body text-sm">
                <AwardBadge size="sm" align="center" tone="cream" />
                <div className="flex flex-col items-center gap-2 pt-1 mt-2 border-t border-cvc-cream/15 w-full">
                  <span className="flex items-center gap-2 pt-3"><MapPin size={16} weight="duotone" /> Logan, Utah</span>
                  <span className="flex items-center gap-2"><Phone size={16} weight="duotone" /> 435-535-7000</span>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — Prominent ScaledEasy alternative card
              pushed down (lg:mt-52) so the top-right "Voted Best Business Consulting"
              award badge overlay has clean breathing room above it. */}
          <aside className="lg:col-span-5 lg:mt-24">
            <div
              data-testid="hero-scaled-card"
              className="rounded-3xl bg-[#D8D0B8] p-6 lg:p-7 shadow-2xl relative overflow-hidden"
            >
              <div className="absolute -bottom-20 -right-12 w-72 h-72 rounded-full bg-white/5 blur-3xl" />
              <div className="relative">
                <p className="text-[10px] tracking-[0.3em] uppercase font-body font-semibold text-cvc-ink/85">Not in Logan? · Nationwide alternative</p>
                <img src={SCALED_LOGO} alt="Scaled — ScaledEasy.com" data-testid="hero-scaled-logo" className="mt-3 w-28 h-auto" />
                <h2 className="font-heading text-2xl lg:text-3xl mt-3 leading-tight text-cvc-ink">
                  The same 100-step scale-up — delivered by phone.
                </h2>
                <div className="mt-4 flex items-start gap-4">
                  <img
                    src={CALI_SPECIALIST}
                    alt="ScaledEasy specialist on a client call at her workstation."
                    loading="lazy"
                    data-testid="hero-scaled-specialist-photo"
                    className="w-28 h-28 sm:w-32 sm:h-32 object-cover rounded-xl border-2 border-scaled-green/25 shadow-lg shrink-0"
                  />
                  <p className="font-body text-sm text-cvc-ink leading-relaxed">
                    <span className="font-semibold text-cvc-ink">ScaledEasy.com</span> is Cache Valley Consulting's nationwide sister program — Jonathan's exact playbook, delivered by trained specialists over the phone.
                  </p>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <a href="https://www.scaledeasy.com/" target="_blank" rel="noopener noreferrer">
                    <Button className="bg-white text-scaled-green hover:bg-cvc-cream rounded-full px-5 py-4 h-auto font-body text-sm">
                      <PhoneCall size={15} weight="duotone" className="mr-2" /> Visit ScaledEasy.com
                    </Button>
                  </a>
                  <span className="font-body text-xs text-cvc-ink-soft">Phone-based · National</span>
                </div>
                <p className="font-body text-xs text-cvc-ink-soft leading-relaxed mt-4 pt-3 border-t border-scaled-green/20">
                  <strong className="text-cvc-ink">Local in Logan?</strong> Jonathan can meet with you in person — keep scrolling.
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </header>
  );
}
